// The aim is to deliver a soft parsing experience, in which invalid query strings deliver an empty result
import {
    HibuEqualsFilter,
    HibuFacetFilter,
    HibuFacetFilterFromUrl,
    HibuRangeFilter,
    ReduxFacetGroup,
    ReduxSelectedFacetValueByFieldName,
    ReduxSelectedFacetValuesByFieldName,
    SelectionType
} from '../../facets/facetTypes';
import {QUERY_ENTRY_SEPARATOR, URL_FILTER_PARAM} from '../const';
import {initUnknownFacetGroup} from '../../facets/facetUtils';

const QUERY_PARAM_REG_EXP = /([a-zA-Z\d._]+)\[(.+)]/;

/**
 * Parses string of the form expressed below in the example.
 * @param {string} facetUrlParams
 * @return HibuFacetFilterFromUrl
 * @example
 * const q_params = parseFacetFilterString('doctype[pdf|word]lang[de, |en],person[!max%20smith|steve]&s=title')
 */
export const parseFacetFilterString = (facetUrlParams?: string): HibuFacetFilterFromUrl => {
    const hibuFilters: HibuFacetFilter = {};
    const filterValuesByFieldName: ReduxSelectedFacetValueByFieldName = {};
    if (!facetUrlParams) {
        return {hibuFilters, filterValuesByFieldName};
    }

    for (const facetUrlParam of facetUrlParams.split(QUERY_ENTRY_SEPARATOR)) {
        const matches = facetUrlParam.match(QUERY_PARAM_REG_EXP);
        const queryParamName = matches ? matches[1] : null;
        if (queryParamName) {
            if (!hibuFilters[queryParamName]) {
                hibuFilters[queryParamName] = [];
            }
            if (!filterValuesByFieldName[queryParamName]) {
                filterValuesByFieldName[queryParamName] = [];
            }
            const queryParamValueString = matches ? matches[2] : '';
            const queryParamValues = queryParamValueString.split('|');

            for (let i = 0; i < queryParamValues.length; i++) {
                const queryParamRangeValues = queryParamValues[i].split('~');
                if (queryParamRangeValues.length === 2) {
                    const v1 = decodeURI(queryParamRangeValues[0]);
                    const v2 = decodeURI(queryParamRangeValues[1]);
                    hibuFilters[queryParamName].push(new HibuRangeFilter(v1, v2));
                    filterValuesByFieldName[queryParamName] = [v1, v2];  //TODO, if multiple ranges are supported in UI, this needs to be changed.
                } else {
                    hibuFilters[queryParamName].push(new HibuEqualsFilter(decodeURI(queryParamValues[i])));
                    filterValuesByFieldName[queryParamName].push(decodeURI(queryParamValues[i]));
                }
            }
        }
    }
    return {hibuFilters, filterValuesByFieldName};
};

/**
 * Builds a string out of the fieldNames and the corresponding values of the selected facets
 * @param selectedFacetsByFieldName facets, that need to be represented in the url
 * @param facetGroup needed to see if a facetFilter is of type RANGE or MULTI
 * @return {string} selected facets as string for building the url
 */
export const buildFacetFilterString = (selectedFacetsByFieldName: ReduxSelectedFacetValuesByFieldName, facetGroup: ReduxFacetGroup): string => {

    const encoder = (s: string): string => {
        return encodeURI(s)
            .replaceAll('!', '%21')
            .replaceAll('&', '%24')
            .replaceAll('-', '%2D');
    };

    const stringParts: string[] = [];
    if (facetGroup) {
        const fieldNames = selectedFacetsByFieldName.allKeys;
        fieldNames.forEach((fieldName, index0) => {
            const facetGroupElement = facetGroup[fieldName] ? facetGroup[fieldName] : initUnknownFacetGroup(fieldName);
            stringParts.push(fieldName, encodeURIComponent('['));
            const valuesByFieldName: string[] = selectedFacetsByFieldName.byKey[fieldName];
            valuesByFieldName && valuesByFieldName.forEach((value, index) => {
                stringParts.push(encoder(value));
                if (index !== valuesByFieldName.length - 1) {
                    if (facetGroupElement.selectionType === SelectionType.RANGE) {
                        stringParts.push(encodeURIComponent('~')); //TODO, if multiple ranges are supported in UI, this needs to be changed.
                    } else if (facetGroupElement.selectionType === SelectionType.MULTI) {
                        stringParts.push(encodeURIComponent('|'));
                    }
                }
            });

            stringParts.push(encodeURIComponent(']'));
            if (index0 !== fieldNames.length - 1) {
                stringParts.push(encodeURIComponent(','));
            }
        });
    }
    return stringParts.length > 0 ? `${URL_FILTER_PARAM}=` + stringParts.join('') : '';
};

